import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  Search,
  Terminal,
  Database,
  RefreshCw,
  AlertCircle,
  Folder,
  Server,
  ChevronsDown,
  ChevronsUp,
  Upload,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { datasetApi } from '../../api/datasetApi';
import { udfApi } from '../../api/udfApi';
import { useAuth } from '../../context/AuthContext';
import {
  InventoryTreeNode,
  buildSortedDatasetTree,
  buildSortedFunctionTree,
  collectItemsTreeNodeIds,
  DatasetInventoryTree,
  datasetLeafItem,
  datasetLeafIcon,
  filterDatasetTree,
  hasManteoDatasets,
} from './inventoryTreeShared';

const ImportFromDataSourcesCta = ({ onClose, variant = 'primary', className }) => (
  <Link
    to="/sources"
    onClick={onClose}
    className={cn(
      'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors shrink-0',
      variant === 'primary'
        ? 'bg-emerald-600 px-3.5 py-2 text-sm text-white shadow-sm shadow-emerald-600/25 hover:bg-emerald-700'
        : 'border border-emerald-200 bg-white px-3 py-1.5 text-xs text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/60',
      className
    )}
  >
    <Upload size={variant === 'primary' ? 15 : 14} strokeWidth={2.25} />
    <span>Add more datasets</span>
  </Link>
);

const AddItemModal = ({ isOpen, onClose, onSelect, currentItemIds = [], type = 'dataset' }) => {
  const { user } = useAuth();
  const userFolderLabel = user?.email || 'My data';
  const [searchQuery, setSearchQuery] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [itemsTree, setItemsTree] = useState({ manteo: [], bigquery: {}, custom: [] });
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const treeExpandedOnceRef = useRef(false);

  const isDataset = type === 'dataset';
  const Icon = isDataset ? Database : Terminal;
  const title = isDataset ? 'Link imported dataset' : 'Add imported function';
  const subtitle = isDataset
    ? 'Already imported, select below to link to workspace'
    : 'Select from your library';
  const placeholder = isDataset ? 'Search your imported datasets...' : 'Search functions...';
  const emptyMessage = isDataset ? 'No imported datasets yet' : 'No functions found';
  const emptySubmessage = isDataset
    ? 'Upload files or connect BigQuery on Data Sources first, then return here to link them to this workspace.'
    : 'Import functions first to add them to your workspace';
  const loadItems = useCallback(async () => {
    if (!isOpen) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = isDataset ? await datasetApi.getDatasets() : await udfApi.listUDFs();
      setAllItems(data);
    } catch (err) {
      console.error(`Failed to load ${type}s:`, err);
      setError(`Failed to load ${type}s from your library.`);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, isDataset, type]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedItemIds(new Set());
      treeExpandedOnceRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    treeExpandedOnceRef.current = false;
  }, [type]);

  useEffect(() => {
    const itemsList = Array.isArray(allItems) ? allItems : [];
    const tree = isDataset
      ? { ...buildSortedDatasetTree(itemsList), custom: [] }
      : { manteo: [], ...buildSortedFunctionTree(itemsList) };

    setItemsTree(tree);

    if (!treeExpandedOnceRef.current) {
      const nodeIds = collectItemsTreeNodeIds(tree);
      if (nodeIds.size > 0) {
        setExpandedNodes(nodeIds);
        treeExpandedOnceRef.current = true;
      }
    }
  }, [allItems, isDataset]);

  const toggleNode = (nodeId) => {
    const next = new Set(expandedNodes);
    next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
    setExpandedNodes(next);
  };

  const expandAll = () => {
    setExpandedNodes(collectItemsTreeNodeIds(itemsTree));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const toggleSelection = (item) => {
    if (currentItemIds.includes(item.id)) return;

    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
  };

  const handleAddSelected = async () => {
    const selectedItems = allItems.filter(item => selectedItemIds.has(item.id));
    if (selectedItems.length > 0) {
      if (isDataset) {
        // Add datasets as a batch (like functions)
        onSelect('library', selectedItems);
      } else {
        // Add functions as a batch
        onSelect('udf-library', selectedItems);
      }
      setSelectedItemIds(new Set());
      onClose();
    }
  };

  // Filter tree based on search query
  const baseDatasetTree = isDataset ? buildSortedDatasetTree(allItems) : null;
  const filteredDatasetTree = isDataset
    ? filterDatasetTree(baseDatasetTree, searchQuery)
    : null;

  const filteredTree = isDataset
    ? { ...filteredDatasetTree, custom: [] }
    : {
        manteo: { 'user-data': [], 'data-catalog': [] },
        bigquery: Object.fromEntries(
          Object.entries(itemsTree.bigquery).map(([projectId, datasets]) => {
            const filteredDatasets = Object.fromEntries(
              Object.entries(datasets).map(([datasetId, items]) => [
                datasetId,
                items.filter(item => {
                  const q = searchQuery.toLowerCase();
                  return (item.functionName || item.name || '').toLowerCase().includes(q) ||
                         (item.signature || '').toLowerCase().includes(q) ||
                         (item.description || '').toLowerCase().includes(q);
                })
              ]).filter(([, items]) => items.length > 0)
            );
            return [projectId, filteredDatasets];
          }).filter(([, datasets]) => Object.keys(datasets).length > 0)
        ),
        custom: itemsTree.custom.filter(item => {
          const q = searchQuery.toLowerCase();
          return (item.name || '').toLowerCase().includes(q) ||
                 (item.signature || '').toLowerCase().includes(q) ||
                 (item.description || '').toLowerCase().includes(q);
        }),
      };

  const hasBigQuery = Object.keys(filteredTree.bigquery).length > 0;
  const hasManteo = isDataset ? hasManteoDatasets(filteredTree.manteo) : false;
  const hasCustom = filteredTree.custom.length > 0;
  const hasAnyItems = hasManteo || hasBigQuery || hasCustom;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <div
        className="absolute inset-0 bg-gray-950/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        aria-labelledby="add-item-modal-title"
        className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-950 max-h-[min(640px,88vh)]"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <Icon size={16} />
              </div>
              <div className="min-w-0">
                <h3 id="add-item-modal-title" className="text-base font-semibold text-gray-900 dark:text-white truncate">
                  {title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={loadItems}
              disabled={isLoading}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              title="Refresh"
            >
              <RefreshCw size={16} className={cn(isLoading && 'animate-spin')} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-3 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-emerald-500/60 focus:bg-white focus:ring-2 focus:ring-emerald-500/15 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:focus:bg-gray-950"
            />
          </div>
          {hasAnyItems && (
            <>
              <button
                type="button"
                onClick={expandAll}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                title="Expand all"
              >
                <ChevronsDown size={16} />
              </button>
              <button
                type="button"
                onClick={collapseAll}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                title="Collapse all"
              >
                <ChevronsUp size={16} />
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="mx-3 mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900/50 dark:bg-red-950/30">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
            <p className="flex-1 text-xs text-red-600 dark:text-red-300">{error}</p>
            <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X size={14} />
            </button>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 py-2 custom-scrollbar min-h-[200px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
              <p className="mt-3 text-xs text-gray-500">Loading…</p>
            </div>
          ) : !hasAnyItems ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 px-4 py-12 text-center dark:border-gray-800">
              <Icon size={28} className="mb-3 text-gray-300 dark:text-gray-700" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{emptyMessage}</p>
              <p className="mt-1 max-w-xs text-xs text-gray-500">{emptySubmessage}</p>
              {isDataset && (
                <ImportFromDataSourcesCta onClose={onClose} variant="primary" className="mt-4" />
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-gray-100 dark:border-gray-800/80 divide-y divide-gray-100 dark:divide-gray-800/80">
              {isDataset && (hasManteo || hasBigQuery) && (
                <DatasetInventoryTree
                  tree={filteredTree}
                  userFolderLabel={userFolderLabel}
                  expandedNodes={expandedNodes}
                  onToggleNode={toggleNode}
                  renderDatasetLeaf={(item, level) => {
                    const isAlreadyAdded = currentItemIds.includes(item.id);
                    const isSelected = selectedItemIds.has(item.id);
                    const isBigQuery = item.type === 'bigquery';
                    return (
                      <InventoryTreeNode
                        key={item.id}
                        item={datasetLeafItem(item)}
                        level={level}
                        onSelect={() => !isAlreadyAdded && toggleSelection(item)}
                        icon={datasetLeafIcon}
                        iconColor={isBigQuery ? 'text-blue-500/60' : 'text-emerald-500/70'}
                        isSelected={isSelected}
                        isDisabled={isAlreadyAdded || (item.status && item.status !== 'ready')}
                        isAlreadyAdded={isAlreadyAdded}
                      />
                    );
                  }}
                />
              )}

              {!isDataset && hasBigQuery && (
                <InventoryTreeNode
                  item={{ id: 'bigquery', name: 'BigQuery' }}
                  level={0}
                  isExpanded={expandedNodes.has('bigquery')}
                  onToggle={() => toggleNode('bigquery')}
                  icon={Server}
                  iconColor="text-blue-500"
                  children={
                    <div>
                      {Object.entries(filteredTree.bigquery).map(([projectId, datasets]) => (
                        <InventoryTreeNode
                          key={projectId}
                          item={{ id: projectId, name: projectId }}
                          level={1}
                          isExpanded={expandedNodes.has(projectId)}
                          onToggle={() => toggleNode(projectId)}
                          icon={Folder}
                          iconColor="text-blue-500"
                          children={
                            <div>
                              {Object.entries(datasets).map(([datasetId, items]) => (
                                <InventoryTreeNode
                                  key={datasetId}
                                  item={{ id: datasetId, name: datasetId }}
                                  level={2}
                                  isExpanded={expandedNodes.has(datasetId)}
                                  onToggle={() => toggleNode(datasetId)}
                                  icon={Folder}
                                  iconColor="text-blue-400"
                                  children={
                                    <div>
                                      {items.map(item => {
                                        const isAlreadyAdded = currentItemIds.includes(item.id);
                                        const isSelected = selectedItemIds.has(item.id);
                                        return (
                                          <InventoryTreeNode
                                            key={item.id}
                                            item={{
                                              id: item.id,
                                              name: item.functionName || item.name,
                                              signature: item.signature,
                                              status: item.status,
                                              created_at: item.created_at,
                                            }}
                                            level={3}
                                            onSelect={() => !isAlreadyAdded && toggleSelection(item)}
                                            icon={Terminal}
                                            iconColor="text-blue-500/60"
                                            isSelected={isSelected}
                                            isDisabled={isAlreadyAdded || (item.status && item.status !== 'ready')}
                                            isAlreadyAdded={isAlreadyAdded}
                                          />
                                        );
                                      })}
                                    </div>
                                  }
                                />
                              ))}
                            </div>
                          }
                        />
                      ))}
                    </div>
                  }
                />
              )}

              {!isDataset && hasCustom && (
                <InventoryTreeNode
                  item={{ id: 'custom', name: 'Custom functions' }}
                  level={0}
                  isExpanded={expandedNodes.has('custom')}
                  onToggle={() => toggleNode('custom')}
                  icon={Folder}
                  iconColor="text-emerald-500"
                  children={
                    <div>
                      {filteredTree.custom.map(item => {
                        const isAlreadyAdded = currentItemIds.includes(item.id);
                        const isSelected = selectedItemIds.has(item.id);
                        return (
                          <InventoryTreeNode
                            key={item.id}
                            item={{
                              id: item.id,
                              name: item.name,
                              signature: item.signature,
                              created_at: item.created_at,
                            }}
                            level={1}
                            onSelect={() => !isAlreadyAdded && toggleSelection(item)}
                            icon={Terminal}
                            iconColor="text-emerald-500/70"
                            isSelected={isSelected}
                            isDisabled={isAlreadyAdded}
                            isAlreadyAdded={isAlreadyAdded}
                          />
                        );
                      })}
                    </div>
                  }
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/80 px-4 py-3 dark:border-gray-800 dark:bg-gray-900/40">
          <p className="min-w-0 text-xs text-gray-500 dark:text-gray-400">
            {selectedItemIds.size > 0
              ? `${selectedItemIds.size} selected`
              : isDataset
                ? 'Need more datasets?'
                : 'Need more functions'}
          </p>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {isDataset && (
              <ImportFromDataSourcesCta
                onClose={onClose}
                variant={selectedItemIds.size > 0 ? 'secondary' : 'primary'}
              />
            )}
            {selectedItemIds.size > 0 && (
              <button
                type="button"
                onClick={handleAddSelected}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              >
                Add {selectedItemIds.size} to workspace
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;
